import * as React from 'react';
import { IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import { renderToStaticMarkup } from 'react-dom/server';
import { useSelector } from 'react-redux';

import { MapBalloon } from '../../../components/Map/parts';
import { getIsDistanceCalculatingEnabled } from '../../../selectors/distanceCalculating';
import {
  IRegularInfrastructureData,
  IRecommendationNewbuildingInfrastructure,
  ITAInfrastructureData,
  TTAInfrastructureType,
  TInfrastructureItem,
} from '../../../types/map';
import { findGeoObjectDatum } from '../helpers';

interface IUseGeoObjectBalloonParams {
  regularInfrastructureByYearRef: React.MutableRefObject<Map<number, IRegularInfrastructureData>>;
  transportAccessibilityInfrastructureByTypeRef: React.MutableRefObject<
    Map<TTAInfrastructureType, ITAInfrastructureData>
  >;
  onRouteButtonClick(item: TInfrastructureItem): void;
  onSimilarNewbuildingClick(item: IRecommendationNewbuildingInfrastructure): void;
  resetTARouting: () => void;
}

const ROUTE_BUTTON_PREFIX = 'route-button-';

export const useGeoObjectBalloon = ({
  regularInfrastructureByYearRef,
  transportAccessibilityInfrastructureByTypeRef,
  onRouteButtonClick,
  onSimilarNewbuildingClick,
  resetTARouting,
}: IUseGeoObjectBalloonParams) => {
  const isDistanceCalculatingEnabled = useSelector(getIsDistanceCalculatingEnabled);

  const handleBalloonHtmlFirstFetch = React.useCallback(
    async (geoObject: IMapGeoObject): Promise<string> => {
      const {
        options: { id },
      } = geoObject;

      const datum = findGeoObjectDatum(
        id,
        regularInfrastructureByYearRef.current,
        transportAccessibilityInfrastructureByTypeRef.current,
      );
      const item = datum?.infrastructureItem;

      if (!item) {
        return Promise.reject(new Error('Не верный тип пина!'));
      }

      const markup = renderToStaticMarkup(
        <MapBalloon
          infrastructureItem={item}
          isDistanceCalculatingEnabled={isDistanceCalculatingEnabled}
          routeButtonIdPrefix={ROUTE_BUTTON_PREFIX}
        />,
      );

      return Promise.resolve(markup);
    },
    [isDistanceCalculatingEnabled],
  );

  const handleBalloonOpen = React.useCallback(
    (geoObject: IMapGeoObject) => {
      const datum = findGeoObjectDatum(
        geoObject.options.id,
        regularInfrastructureByYearRef.current,
        transportAccessibilityInfrastructureByTypeRef.current,
      );
      const item = datum?.infrastructureItem as TInfrastructureItem;

      const routeButton = document.getElementById(ROUTE_BUTTON_PREFIX + item.id);

      if (routeButton) {
        routeButton.addEventListener('click', () => {
          onRouteButtonClick(item);
        });
      }

      if (item.type === 'similar_newbuilding') {
        const links = document.querySelectorAll(`#${item.id} a`);

        links.forEach(link =>
          link.addEventListener('click', () => {
            onSimilarNewbuildingClick(item);
          }),
        );
      }

      resetTARouting();
    },
    [onRouteButtonClick, onSimilarNewbuildingClick, resetTARouting],
  );

  return { handleBalloonHtmlFirstFetch, handleBalloonOpen };
};
